import React from "react";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import ConstructionSitesWidget from "../Dashboard/Widgets/ConstructionSitesWidget";
import RentaTasksWizardPage from "../RentaTasksWizardPage";
import GetConstructionSitesDataRequest from "@/models/server/requests/GetConstructionSitesDataRequest";
import ConstructionSiteOrWarehouse from "@/models/server/ConstructionSiteOrWarehouse";
import {ConstructionSiteOrWarehouseType} from "@/models/Enums";
import Localizer from "../../../localization/Localizer";
import UnleashHelper from "@/helpers/UnleashHelper";
import FeatureFlags from "@/helpers/FeatureFlags";
import UserDefaultWorkOrderValues from "@/models/server/UserDefaultWorkOrderValues";

export interface ISelectConstructionSiteProps {
}

interface ISelectConstructionSiteState {
    constructionSites: ConstructionSiteOrWarehouse[];
}

export default class SelectConstructionSite extends RentaTasksWizardPage<ISelectConstructionSiteProps, ISelectConstructionSiteState> {

    private readonly ConstructionSitesId = "ConstructionSites";

    state: ISelectConstructionSiteState = {
        constructionSites: []
    };

    private get constructionSites(): ConstructionSitesWidget | null {
        return this.findWidget(this.ConstructionSitesId);
    }

    private async fetchAndSetConstructionSitesAsync(): Promise<void> {
        const request = new GetConstructionSitesDataRequest();
        const allSites: ConstructionSiteOrWarehouse[] = await this.postAsync("/api/rentaTasks/getConstructionSitesData", request);
        const constructionSites = allSites.filter(item => item.type === ConstructionSiteOrWarehouseType.ConstructionSite);

        if (constructionSites.length === 0) {
            await this.alertErrorAsync(Localizer.rentaTasksSelectSiteNoAvailableSites);
        }

        await this.setState({ constructionSites });

        if (this.constructionSites) {
            this.constructionSites.state.data = constructionSites;
            await this.constructionSites.reRenderAsync();
        }

        await this.applyDefaultConstructionSiteAsync(constructionSites);
    }

    private async applyDefaultConstructionSiteAsync(constructionSites: ConstructionSiteOrWarehouse[]): Promise<void> {
        if (this.wizard.workOrder?.id === "" && UnleashHelper.isEnabled(FeatureFlags.DefaultWorkOrderValues) && !this.wizard.owner) {
            const currentUser = this.getUser();
            const defaultValues: UserDefaultWorkOrderValues = await this.postAsync(`/api/rentaTasks/getUserDefaultWorkOrderValues`, currentUser.id);
            if (defaultValues?.constructionSiteId) {
                const defaultSite = constructionSites.find(site => site.id === defaultValues.constructionSiteId);
                if (defaultSite) {
                    this.wizard.owner = defaultSite;                        this.saveContext();
                    await this.reRenderAsync();

                    if (this.constructionSites) {
                        await this.constructionSites.selectItemAsync(defaultSite);
                    }
                }
            }
        }
    }

    private async selectConstructionSiteAsync(constructionSite: ConstructionSiteOrWarehouse, userInteraction: boolean): Promise<void> {

        this.wizard.owner = constructionSite;

        this.saveContext();

        if (userInteraction) {
            await this.nextAsync();
        }
    }

    private async onSelectConstructionSiteAsync(constructionSite: ConstructionSiteOrWarehouse | null, userInteraction: boolean): Promise<void> {
        if (constructionSite != null) {
            await this.selectConstructionSiteAsync(constructionSite, userInteraction);
        }
    }

    private get selectedItem(): ConstructionSiteOrWarehouse | null {
        if (UnleashHelper.isEnabled(FeatureFlags.DefaultWorkOrderValues) && this.wizard.owner) {
            return this.wizard.owner;
        }

        return (Utility.geoEnabled)
            ? null
            : this.wizard.owner;
    }

    protected get workOrderRequired(): boolean {
        return false;
    }

    public async componentDidMount(): Promise<void> {
        await super.componentDidMount();
        await this.fetchAndSetConstructionSitesAsync();
    }

    public async nextAsync(): Promise<void> {
        if ((this.constructionSites != null) && (this.constructionSites!.selectedItem != null)) {
            await super.nextAsync();
        }
    }

    public getManual(): string {
        return Localizer.selectConstructionSiteGetManual;
    }

    public renderContent(): React.ReactNode {

        return (
            <React.Fragment>

                <ConstructionSitesWidget id={this.ConstructionSitesId} required stretchContent wide
                                         label={Localizer.selectConstructionSiteConstructionSite}
                                         description={Localizer.selectConstructionSiteConstructionSiteDescription}
                                         selectedItem={this.selectedItem || undefined}
                                         items={this.state.constructionSites}
                                         onChange={(_, item, userInteraction) => this.onSelectConstructionSiteAsync(item, userInteraction)}
                />

            </React.Fragment>
        );
    }
}